import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

const ConfirmContext = createContext(null);

// Hook used by consumers
export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    // Do not fall back silently — make the problem obvious
    throw new Error("useConfirm() called outside of <ConfirmProvider>. Mount ConfirmProvider near the app root.");
  }
  return ctx;
}

export default function ConfirmProvider({ children }) {
  const resolverRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState({
    title: "Are you sure?",
    message: "",
    confirmText: "OK",
    cancelText: "Cancel",
    variant: "default",
  });

  const confirm = useCallback((next = {}) => {
    setOpts((prev) => ({ ...prev, ...next }));
    setOpen(true);
    return new Promise((resolve) => {
      resolverRef.current = (result) => {
        resolverRef.current = null;
        setOpen(false);
        resolve(result);
      };
    });
  }, []);

  const value = useMemo(() => confirm, [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {open &&
        createPortal(
          <div aria-modal="true" role="dialog" className="fixed inset-0 z-[9999] flex items-center justify-center">
            <div className="absolute inset-0 bg-black/60" onClick={() => resolverRef.current?.(false)} />
            <div
              className="relative w-full max-w-md rounded-lg shadow-xl p-5"
              style={{ background: "var(--brand-surface, #121a26)", color: "var(--brand-text, #e6edf7)" }}
            >
              <h3 className="text-lg font-semibold mb-2">{opts.title}</h3>
              {opts.message && <p className="text-sm opacity-80 mb-4">{opts.message}</p>}
              <div className="flex justify-end gap-2">
                <button
                  className="px-3 py-2 rounded bg-slate-600 hover:bg-slate-500"
                  onClick={() => resolverRef.current?.(false)}
                >
                  {opts.cancelText || "Cancel"}
                </button>
                <button
                  className="px-3 py-2 rounded text-white"
                  style={{
                    backgroundColor: opts.variant === "danger" ? "#dc2626" : "var(--brand-accent, #00c2ff)",
                  }}
                  onClick={() => resolverRef.current?.(true)}
                >
                  {opts.confirmText || "OK"}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </ConfirmContext.Provider>
  );
}
